import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import StepSelector from './components/StepSelector';
import OrderSummary from './components/OrderSummary';
import { studentPromoCodes, STUDENT_DISCOUNT_REAL } from '../../mocks/studentCodes';
import EstudiantesSeoJsonLd from './components/SeoJsonLd';

interface SelectedProduct {
  id: string;
  name: string;
  price: number;
  oldPrice?: number;
  image: string;
  type: string;
}

export default function EstudiantesPage() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [selectedOffice, setSelectedOffice] = useState<SelectedProduct | null>(null);
  const [selectedAntivirus, setSelectedAntivirus] = useState<SelectedProduct | null>(null);
  const [selectedVpn, setSelectedVpn] = useState<SelectedProduct | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Productos Office 365
  const officeProducts = [
    {
      id: 'stu-off2026ltsc1',
      name: 'Office 365 última versión',
      price: 149990,
      oldPrice: 374975,
      discount: '-60%',
      image: 'https://public.readdy.ai/ai/img_res/0c37555de554fb2159ddcb0247f6317f.jpg',
      type: 'Suscripción anual - 1 usuario',
      description: 'Word, Excel, PowerPoint, Outlook, Teams, OneDrive 1TB',
      badge: 'Mejor Opción',
      badgeColor: 'bg-emerald-500',
      bestOption: true,
    },
    {
      id: 'stu-off2024ltsc1',
      name: 'Office 2024 Pro Plus LTSC 1 Cuenta',
      price: 89900,
      oldPrice: 229900,
      discount: '-61%',
      image: 'https://readdy.ai/api/search-image?query=striking%20Microsoft%20Office%202024%20LTSC%20Professional%20Plus%20single%20user%20product%20showcase%20with%20bold%20dark%20orange%20amber%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20card%20sleek%20modern%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20warm%20glowing%20accents%20premium%20software%20display%203D%20render%20professional%20commercial%20photography&width=400&height=400&seq=offltsc2024single1&orientation=squarish',
      type: 'Licencia de por vida - 1 PC',
      description: 'Word, Excel, PowerPoint, Outlook, Access, Publisher',
      badge: 'LTSC',
      badgeColor: 'bg-gradient-to-r from-gray-600 to-gray-500',
    },
    {
      id: 'stu-off2024pro',
      name: 'Office 2024 Professional Plus',
      price: 69900,
      oldPrice: 229900,
      discount: '-70%',
      image: 'https://readdy.ai/api/search-image?query=vibrant%20modern%20Microsoft%20Office%202024%20Professional%20Plus%20product%20showcase%20with%20glowing%20orange%20red%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20card%20sleek%20futuristic%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20light%20rays%20premium%20software%20packaging%203D%20render%20high%20quality%20commercial%20photography&width=400&height=400&seq=offpro2024new1&orientation=squarish',
      type: 'Licencia de por vida - 1 PC',
      description: 'Word, Excel, PowerPoint, Outlook, Access, Publisher',
      badge: 'Más Vendido',
      badgeColor: 'bg-gradient-to-r from-[#D24726] to-[#e85d3a]',
    },
    {
      id: 'stu-off2021ltsc1',
      name: 'Office 2021 Pro Plus LTSC 1 Cuenta',
      price: 59950,
      oldPrice: 219900,
      discount: '-73%',
      image: 'https://readdy.ai/api/search-image?query=elegant%20Microsoft%20Office%202021%20LTSC%20Professional%20Plus%20single%20user%20enterprise%20edition%20product%20display%20with%20sophisticated%20silver%20charcoal%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20modern%20corporate%20design%20dramatic%20lighting%20dark%20background%20with%20metallic%20shine%20effects%20premium%20business%20software%20packaging%203D%20visualization%20high%20end%20commercial%20photography&width=400&height=400&seq=offltsc2021single1&orientation=squarish',
      type: 'Licencia de por vida - 1 PC',
      description: 'Word, Excel, PowerPoint, Outlook, Access, Publisher',
      badge: 'LTSC',
      badgeColor: 'bg-gradient-to-r from-gray-600 to-gray-500',
    },
    {
      id: 'stu-off2021pro',
      name: 'Office 2021 Professional Plus',
      price: 49950,
      oldPrice: 199900,
      discount: '-75%',
      image: 'https://readdy.ai/api/search-image?query=impressive%20Microsoft%20Office%202021%20Professional%20Plus%20product%20display%20with%20deep%20blue%20navy%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20sophisticated%20modern%20design%20dramatic%20lighting%20dark%20elegant%20background%20with%20light%20effects%20premium%20software%20packaging%203D%20visualization%20high%20quality%20commercial%20photography&width=400&height=400&seq=offpro2021new1&orientation=squarish',
      type: 'Licencia de por vida - 1 PC',
      description: 'Word, Excel, PowerPoint, Outlook, Access, Publisher',
      badge: 'Mejor Precio',
      badgeColor: 'bg-gradient-to-r from-emerald-500 to-green-500',
    },
    {
      id: 'stu-off2021hb',
      name: 'Office 2021 Home & Business',
      price: 370953,
      oldPrice: 706563,
      discount: '-47%',
      image: 'https://readdy.ai/api/search-image?query=eye%20catching%20Microsoft%20Office%202021%20Home%20Business%20product%20showcase%20with%20bright%20emerald%20green%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20modern%20sleek%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20glowing%20accents%20premium%20software%20display%203D%20render%20professional%20commercial%20photography&width=400&height=400&seq=offhb2021new1&orientation=squarish',
      type: 'Licencia de por vida - 5 PC',
      description: 'Word, Excel, PowerPoint, Outlook',
    },
  ];

  // Productos Antivirus
  const antivirusProducts = [
    {
      id: 'stu-kasp-std-1',
      name: 'Kaspersky Standard 1 Dispositivo - 1 año',
      price: 115600,
      oldPrice: 195000,
      discount: '-41%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20standard%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2b&orientation=squarish',
      type: '1 Dispositivo - 1 año',
      description: 'Protección esencial contra malware y virus',
      salesCount: 29,
    },
    {
      id: 'stu-kasp-std-5',
      name: 'Kaspersky Standard 5 Dispositivos - 1 año',
      price: 229450,
      oldPrice: 550000,
      discount: '-58%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20standard%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2&orientation=squarish',
      type: '5 Dispositivos - 1 año',
      description: 'Protección esencial para 5 dispositivos durante 1 año',
      salesCount: 45,
    },
    {
      id: 'stu-kasp-plus-1-2y',
      name: 'Kaspersky Plus 1 Dispositivo - 2 años',
      price: 143750,
      oldPrice: 275000,
      discount: '-48%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20plus%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av3a&orientation=squarish',
      type: '1 Dispositivo - 2 años',
      description: 'Protección avanzada + VPN limitada + optimización',
      salesCount: 67,
    },
    {
      id: 'stu-kasp-plus-5-2y',
      name: 'Kaspersky Plus 5 Dispositivos - 2 años',
      price: 264900,
      oldPrice: 630000,
      discount: '-58%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20plus%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av3b&orientation=squarish',
      type: '5 Dispositivos - 2 años',
      description: 'Protección avanzada para 5 dispositivos durante 2 años',
      badge: 'Mejor Valor',
      badgeColor: 'bg-teal-500',
      salesCount: 73,
    },
    {
      id: 'stu-kasp-prem-1',
      name: 'Kaspersky Premium 1 Dispositivo - 1 año',
      price: 173100,
      oldPrice: 275000,
      discount: '-37%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20premium%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2c&orientation=squarish',
      type: '1 Dispositivo - 1 año',
      description: 'Protección total premium + gestor contraseñas',
      badge: 'Premium',
      badgeColor: 'bg-amber-500',
      salesCount: 62,
    },
    {
      id: 'stu-kasp-prem-5',
      name: 'Kaspersky Premium 5 Dispositivos - 1 año',
      price: 305200,
      oldPrice: 630000,
      discount: '-52%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20premium%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2d&orientation=squarish',
      type: '5 Dispositivos - 1 año',
      description: 'Protección premium completa para 5 dispositivos',
      badge: 'Más Vendido',
      badgeColor: 'bg-orange-500',
      salesCount: 280,
    },
    {
      id: 'stu-mcafee-1',
      name: 'McAfee Total Protection 1 Dispositivo - 1 año',
      price: 97750,
      oldPrice: 180000,
      discount: '-46%',
      image: 'https://readdy.ai/api/search-image?query=mcafee%20total%20protection%20antivirus%20software%20box%20product%20packaging%20red%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av5&orientation=squarish',
      type: '1 Dispositivo - 1 año',
      description: 'Protección completa McAfee + firewall',
      badge: 'Mejor Precio',
      badgeColor: 'bg-emerald-500',
      salesCount: 56,
    },
    {
      id: 'stu-mcafee-5',
      name: 'McAfee Total Protection 5 Dispositivos - 1 año',
      price: 281750,
      oldPrice: 530530,
      discount: '-47%',
      image: 'https://readdy.ai/api/search-image?query=mcafee%20total%20protection%20antivirus%20software%20box%20product%20packaging%20red%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av5b&orientation=squarish',
      type: '5 Dispositivos - 1 año',
      description: 'Protección total McAfee para 5 dispositivos',
      salesCount: 121,
    },
    {
      id: 'stu-eset-1-2y',
      name: 'ESET NOD32 Antivirus 1 Dispositivo - 2 años',
      price: 109250,
      oldPrice: 230650,
      discount: '-53%',
      image: 'https://readdy.ai/api/search-image?query=eset%20nod32%20antivirus%20software%20box%20product%20packaging%20orange%20teal%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av6&orientation=squarish',
      type: '1 Dispositivo - 2 años',
      description: 'Antivirus ligero y eficiente para estudiantes',
      badge: 'Mejor Opción',
      badgeColor: 'bg-emerald-500',
      bestOption: true,
      salesCount: 45,
    },
    {
      id: 'stu-eset-5-2y',
      name: 'ESET NOD32 Antivirus 5 Dispositivos - 2 años',
      price: 339250,
      oldPrice: 560530,
      discount: '-39%',
      image: 'https://readdy.ai/api/search-image?query=eset%20nod32%20antivirus%20software%20box%20product%20packaging%20orange%20teal%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av6b&orientation=squarish',
      type: '5 Dispositivos - 2 años',
      description: 'Antivirus ESET NOD32 para 5 dispositivos durante 2 años',
      badge: 'Popular',
      badgeColor: 'bg-indigo-500',
      salesCount: 204,
    },
  ];

  // Productos VPN
  const vpnProducts = [
    {
      id: 'stu-adguard-1m',
      name: 'AdGuard VPN 1 Mes - 1 Dispositivo',
      price: 30500,
      oldPrice: 45000,
      discount: '-32%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      type: '1 Dispositivo - 1 mes',
      description: 'Ideal para probar - navegación privada',
      salesCount: 78,
    },
    {
      id: 'stu-adguard-1y',
      name: 'AdGuard VPN 1 Año - 5 Dispositivos',
      price: 21950,
      oldPrice: 45000,
      discount: '-51%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      type: '5 Dispositivos - 1 año',
      description: 'Mejor relación calidad-precio para el año',
      badge: 'Popular',
      badgeColor: 'bg-gradient-to-r from-emerald-500 to-teal-500',
      salesCount: 167,
      priceNote: '/mes',
      multiplyBy: 12,
      licenseText: '5 licencias x 12 meses (1 año)',
    },
    {
      id: 'stu-adguard-2y',
      name: 'AdGuard VPN 2 Años - 10 Dispositivos',
      price: 14950,
      oldPrice: 45000,
      discount: '-71%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      type: '10 Dispositivos - 2 años',
      description: 'Máximo ahorro - cubre toda tu carrera',
      badge: 'Mejor Opción',
      badgeColor: 'bg-gradient-to-r from-teal-500 to-cyan-500',
      bestOption: true,
      salesCount: 283,
      priceNote: '/mes',
      multiplyBy: 24,
      licenseText: '10 licencias x 24 meses (2 años)',
    },
  ];

  const handleSelectOffice = (product: any) => {
    setSelectedOffice(selectedOffice?.id === product.id ? null : {
      id: product.id,
      name: product.name,
      price: product.price,
      oldPrice: product.oldPrice,
      image: product.image,
      type: product.type,
    });
  };

  const handleSelectAntivirus = (product: any) => {
    setSelectedAntivirus(selectedAntivirus?.id === product.id ? null : {
      id: product.id,
      name: product.name,
      price: product.price,
      oldPrice: product.oldPrice,
      image: product.image,
      type: product.type,
    });
  };

  const handleSelectVpn = (product: any) => {
    if (selectedVpn?.id === product.id) {
      setSelectedVpn(null);
    } else {
      const totalPrice = product.multiplyBy ? product.price * product.multiplyBy : product.price;
      const totalOldPrice = product.oldPrice && product.multiplyBy ? product.oldPrice * product.multiplyBy : product.oldPrice;
      setSelectedVpn({
        id: product.id,
        name: product.name,
        price: totalPrice,
        oldPrice: totalOldPrice,
        image: product.image,
        type: product.type,
      });
    }
  };

  const handleApplyPromo = () => {
    setPromoError('');
    const found = studentPromoCodes.find(
      (c) => c.code === promoCode.trim().toUpperCase() && !c.used
    );
    if (found) {
      setPromoApplied(true);
      setPromoError('');
    } else {
      setPromoError('Código inválido o ya utilizado. Verifica e intenta de nuevo.');
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(false);
    setPromoCode('');
    setPromoError('');
  };

  const handleAddToCart = () => {
    const selectedProducts = [selectedOffice, selectedAntivirus, selectedVpn].filter(Boolean);
    if (selectedProducts.length === 0) return;

    const subtotal = selectedProducts.reduce((sum, p) => sum + p!.price, 0);
    const discountAmount = promoApplied ? Math.round(subtotal * (STUDENT_DISCOUNT_REAL / 100)) : 0;

    // Distribuir el descuento proporcionalmente
    selectedProducts.forEach((product, index) => {
      if (!product) return;
      const proportion = product.price / subtotal;
      const itemDiscount = index < selectedProducts.length - 1
        ? Math.round(discountAmount * proportion)
        : discountAmount - selectedProducts.slice(0, -1).reduce((s, p) => s + Math.round(discountAmount * (p!.price / subtotal)), 0);

      addItem({
        id: `pack-est-${product.id}`,
        name: `[Pack Estudiante] ${product.name}`,
        type: product.type,
        price: product.price - itemDiscount,
        oldPrice: product.price,
        image: product.image,
      });
    });

    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      navigate('/carrito');
    }, 1800);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <EstudiantesSeoJsonLd />
      <Header />

      {/* Hero */}
      <section className="relative h-[220px] sm:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20university%20students%20studying%20with%20laptops%20in%20bright%20contemporary%20library%20space%20warm%20ambient%20lighting%20teal%20and%20green%20color%20accents%20young%20diverse%20students%20collaborating%20on%20digital%20projects%20clean%20minimalist%20educational%20environment%20professional%20photography%20cinematic%20composition&width=1920&height=360&seq=studenthero2025v1&orientation=landscape"
          alt="Paquete Estudiantes - Software educativo"
          title="Paquete estudiantes licencias software Colombia"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl w-full">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 backdrop-blur-sm rounded-full px-3 py-1 sm:px-4 sm:py-1.5 mb-3 sm:mb-4">
              <i className="ri-graduation-cap-fill text-teal-300"></i>
              <span className="text-xs sm:text-sm font-medium text-teal-100">Exclusivo para estudiantes</span>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-3 leading-tight">
              Arma tu <span className="text-teal-400">Pack Estudiante</span>
            </h1>
            <p className="text-sm sm:text-lg text-white/90 max-w-xl leading-relaxed mb-3 sm:mb-5">
              Elige Office 365 + Antivirus + VPN a tu medida y obtén un <strong className="text-teal-300">10% de descuento</strong> con tu código de estudiante.
            </p>
            <div className="flex flex-wrap items-center gap-3 sm:gap-5">
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-white/80">
                <i className="ri-shield-check-line text-teal-400"></i>
                <span>Licencias originales</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-white/80">
                <i className="ri-flashlight-line text-teal-400"></i>
                <span>Entrega inmediata</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-white/80">
                <i className="ri-percent-line text-teal-400"></i>
                <span>Descuento exclusivo</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pasos */}
      <section className="py-6 sm:py-10 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {[
              { num: 1, label: 'Office 365', icon: 'ri-file-word-line', done: !!selectedOffice },
              { num: 2, label: 'Antivirus', icon: 'ri-shield-check-line', done: !!selectedAntivirus },
              { num: 3, label: 'VPN', icon: 'ri-global-line', done: !!selectedVpn },
            ].map((step, i) => (
              <div key={step.num} className="flex items-center gap-2 sm:gap-3">
                {i > 0 && <div className={`w-8 sm:w-16 h-0.5 ${step.done || (i === 1 && selectedOffice) || (i === 2 && selectedAntivirus) ? 'bg-teal-500' : 'bg-gray-200'} transition-colors`}></div>}
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                    step.done ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step.done ? <i className="ri-check-line"></i> : step.num}
                  </div>
                  <span className={`text-[10px] sm:text-sm font-medium ${step.done ? 'text-teal-600' : 'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MENSAJE ESPECIAL - ESTUDIANTES EN RUSIA */}
      <section className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800">
        <div className="container mx-auto px-4 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 text-white">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-2xl">
              <span className="text-3xl sm:text-4xl">🌍</span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-base sm:text-lg mb-1 flex items-center gap-2 flex-wrap">
                ¡Futuros estudiantes en Rusia! ✈️
                <span className="bg-teal-500/30 text-teal-200 text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">INFO IMPORTANTE</span>
              </h3>
              <p className="text-xs sm:text-sm leading-relaxed text-white/80">
                En Rusia existen restricciones de acceso a internet (WiFi y datos móviles) que pueden afectar tu día a día. 
                Para acceder sin problemas a tus clases, recursos y redes sociales, necesitas una <strong className="text-teal-300 underline underline-offset-2">VPN confiable</strong>. 
                Combínala con Office y un buen antivirus para estar 100% preparado. <strong className="text-teal-300">¡Estos 3 productos son tu solución!</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-1 py-6 sm:py-10">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Selectores */}
            <div className="flex-1 min-w-0">
              <StepSelector
                stepNumber={1}
                title="Elige tu Office 365"
                subtitle="Documentos, presentaciones y hojas de cálculo para tus trabajos universitarios"
                icon="ri-file-word-line"
                iconColor="bg-gradient-to-br from-orange-500 to-red-500"
                products={officeProducts}
                selectedId={selectedOffice?.id || null}
                onSelect={handleSelectOffice}
                variant="card"
              />

              {/* Referencia Office LTSC vs Normal */}
              <div className="mb-6 sm:mb-8 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center">
                    <i className="ri-information-line text-white text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-gray-900 mb-2">¿Qué diferencia hay entre Office LTSC y Normal?</h4>
                    <div className="space-y-2 text-xs text-gray-600">
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-gray-600 flex-shrink-0 mt-0.5"></i>
                        <p><strong className="text-gray-800">Office LTSC:</strong> Versión completa PREMIUM con soporte extendido (Long-Term Servicing Channel). Actualizaciones de seguridad por 5 años, sin cambios de funciones. Ideal para estabilidad.</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-gray-600 flex-shrink-0 mt-0.5"></i>
                        <p><strong className="text-gray-800">Office Normal:</strong> Versión estándar con todas las funciones. Actualizaciones de seguridad regulares. Perfecto para uso diario.</p>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-gray-700"><strong>Ambas versiones incluyen:</strong> Word, Excel, PowerPoint, Outlook, Access y Publisher — licencia de por vida.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <StepSelector
                stepNumber={2}
                title="Elige tu Antivirus"
                subtitle="Mantén tu PC libre de virus, malware y amenazas mientras navegas y descargas material"
                icon="ri-shield-check-line"
                iconColor="bg-gradient-to-br from-green-500 to-emerald-600"
                products={antivirusProducts}
                selectedId={selectedAntivirus?.id || null}
                onSelect={handleSelectAntivirus}
                variant="card"
              />

              <StepSelector
                stepNumber={3}
                title="Elige tu VPN"
                subtitle="Accede a internet sin restricciones desde cualquier país — ideal si estudias en el extranjero"
                icon="ri-global-line"
                iconColor="bg-gradient-to-br from-teal-500 to-cyan-600"
                products={vpnProducts}
                selectedId={selectedVpn?.id || null}
                onSelect={handleSelectVpn}
                variant="card"
              />
            </div>

            {/* Resumen lateral */}
            <div className="w-full lg:w-[360px] flex-shrink-0">
              <OrderSummary
                office={selectedOffice}
                antivirus={selectedAntivirus}
                vpn={selectedVpn}
                promoCode={promoCode}
                promoApplied={promoApplied}
                promoError={promoError}
                onPromoChange={setPromoCode}
                onApplyPromo={handleApplyPromo}
                onRemovePromo={handleRemovePromo}
                onAddToCart={handleAddToCart}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Beneficios */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-6 sm:mb-10">
            <i className="ri-graduation-cap-line text-teal-600 mr-2"></i>
            Beneficios del Pack Estudiante
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              { icon: 'ri-money-dollar-circle-line', title: 'Ahorra hasta 10%', desc: 'Descuento exclusivo con tu código de estudiante en todo el paquete', color: 'text-teal-600 bg-teal-50' },
              { icon: 'ri-stack-line', title: 'Todo en uno', desc: 'Office + Antivirus + VPN: todo lo que necesitas para estudiar seguro', color: 'text-emerald-600 bg-emerald-50' },
              { icon: 'ri-mail-send-line', title: 'Entrega instantánea', desc: 'Recibe tus licencias por email en minutos, sin esperas', color: 'text-orange-600 bg-orange-50' },
              { icon: 'ri-customer-service-2-line', title: 'Soporte 24/7', desc: 'Ayuda técnica por WhatsApp y Telegram cuando lo necesites', color: 'text-cyan-600 bg-cyan-50' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 sm:p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${item.color} flex items-center justify-center mx-auto mb-3`}>
                  <i className={`${item.icon} text-xl sm:text-2xl`}></i>
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-center max-w-sm w-full animate-[fadeIn_0.3s_ease-out]">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              ¡Pack agregado al carrito!
            </h3>
            <p className="text-sm text-gray-500">
              Redirigiendo al carrito...
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
